import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrg } from "@/hooks/useOrg";
import { useAdminContests } from "@/hooks/useAdminContests";
import { getPresetRange, DatePreset } from "@/lib/dateRanges";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertCircle, Lock, TrendingUp, Users, Trophy, Gift, ArrowUpRight, Info } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    BarChart,
    Bar,
    Cell
} from "recharts";
import { format } from "date-fns";

// Types
interface AnalyticsSummary {
    participants: number;
    total_entries: number;
    avg_entries_per_participant: number;
    referred_participants: number;
    referral_bonus_entries: number;
}

interface AnalyticsData {
    summary: AnalyticsSummary;
    entries_over_time: { date: string; entries: number }[];
    top_entry_methods: { key: string; label: string; entries: number }[];
    top_referrers: { participant_id: string; email: string; referred_count: number }[];
}

const COLORS = ["#0ea5e9", "#f97316", "#10b981", "#8b5cf6", "#f43f5e"];

export default function Analytics() {
    const { toast } = useToast();
    const [searchParams] = useSearchParams();
    const [selectedContestId, setSelectedContestId] = useState<string>(searchParams.get("contestId") || "all");
    const [dateRange, setDateRange] = useState<DatePreset>("30d");

    // 1. Get Org
    const { org, isLoading: isLoadingOrg } = useOrg();
    const isPlanPaid = org?.plan_key === "starter" || org?.plan_key === "growth";

    // 2. Get Contests
    const { data: contests, isLoading: isLoadingContests } = useAdminContests();

    // 3. Fetch Analytics
    const { data: analytics, isLoading: isLoadingAnalytics, error: analyticsError } = useQuery({
        queryKey: ["admin-analytics-data", org?.id, selectedContestId, dateRange],
        enabled: !!org?.id && isPlanPaid,
        queryFn: async () => {
            const { start, end } = getPresetRange(dateRange);

            const payload: any = {
                organization_id: org!.id,
                start_date: start,
                end_date: end,
            };

            if (selectedContestId !== "all") {
                payload.contest_id = selectedContestId;
            }

            const { data, error } = await supabase.functions.invoke("admin-analytics", {
                body: payload,
            });

            if (error) throw error;
            // invoke returns error in data if function returns non-200 usually?
            // supabase-js invoke: if function returns 4xx/5xx, it throws Error if configured?
            // Or returns data as error json.
            // Let's check if data has error.
            if (data && data.error) {
                throw new Error(data.error);
            }
            return data as AnalyticsData;
        },
        staleTime: 60 * 1000, // 60s
        retry: false,
    });

    // Handle Analytics Errors
    if (analyticsError) {
        // We can just show state, but useEffect for toast is cleaner usually, or just inline alert
    }

    // --- Render Functions ---

    if (isLoadingOrg) {
        return <div className="p-8"><Skeleton className="h-96 w-full" /></div>;
    }

    // GATE: Upgrade Required
    if (!isPlanPaid) {
        return (
            <div className="container mx-auto p-8 max-w-4xl">
                <div className="flex items-center gap-3 mb-8">
                    <h1 className="text-3xl font-bold font-heading">Analytics</h1>
                    <Lock className="w-6 h-6 text-muted-foreground" />
                </div>

                <Card className="bg-muted/50 border-dashed">
                    <CardHeader className="text-center py-16">
                        <div className="mx-auto bg-primary/10 p-4 rounded-full w-fit mb-4">
                            <TrendingUp className="w-12 h-12 text-primary" />
                        </div>
                        <CardTitle className="text-2xl mb-2">Unlock Advanced Analytics</CardTitle>
                        <CardDescription className="max-w-md mx-auto mb-8 text-base">
                            Gain deep insights into your contest performance, participant growth, and referral channels.
                            Available on Starter and Growth plans.
                        </CardDescription>
                        <Button onClick={() => window.location.assign("/admin/settings?tab=billing")} size="lg">
                            Upgrade Now
                        </Button>
                    </CardHeader>
                </Card>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-6 md:p-8 space-y-8 animate-in fade-in duration-500">
            {/* Header & Controls */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold font-heading text-foreground">Analytics</h1>
                    <p className="text-muted-foreground">Monitor your contest performance and growth.</p>
                </div>

                <div className="flex items-center gap-3 bg-secondary/30 p-1 rounded-lg">
                    <Select value={selectedContestId} onValueChange={setSelectedContestId}>
                        <SelectTrigger className="w-[200px] bg-background border-none shadow-sm">
                            <SelectValue placeholder="All Contests" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Contests</SelectItem>
                            {contests?.map((c) => (
                                <SelectItem key={c.id} value={c.id}>
                                    {c.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Tabs value={dateRange} onValueChange={(v) => setDateRange(v as any)} className="w-fit">
                        <TabsList className="bg-background shadow-sm">
                            <TabsTrigger value="7d">7D</TabsTrigger>
                            <TabsTrigger value="30d">30D</TabsTrigger>
                            <TabsTrigger value="90d">90D</TabsTrigger>
                        </TabsList>
                    </Tabs>
                </div>
            </div>

            {isLoadingAnalytics ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)}
                    <Skeleton className="col-span-1 lg:col-span-3 h-96 rounded-xl mt-4" />
                    <Skeleton className="col-span-1 lg:col-span-2 h-96 rounded-xl mt-4" />
                </div>
            ) : analyticsError ? (
                <Card className="border-destructive/20 bg-destructive/5">
                    <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                        <AlertCircle className="w-12 h-12 text-destructive mb-4" />
                        <h3 className="text-lg font-semibold text-destructive mb-2">Failed to load analytics</h3>
                        <p className="text-muted-foreground mb-4">{(analyticsError as Error).message}</p>
                        <Button variant="outline" onClick={() => window.location.reload()}>Retry</Button>
                    </CardContent>
                </Card>
            ) : (
                <>
                    {/* KPI Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                        <KpiCard
                            title="Participants"
                            value={analytics?.summary.participants || 0}
                            icon={Users}
                            tooltip="Total unique participants who have entered the giveaway during the selected date range."
                        />
                        <KpiCard
                            title="Total Entries"
                            value={analytics?.summary.total_entries || 0}
                            icon={Trophy}
                            tooltip="Total number of entry actions taken that count as entries (including bonus entries)."
                        />
                        <KpiCard
                            title="Avg. Entries"
                            value={analytics?.summary.avg_entries_per_participant?.toFixed(1) || "0.0"}
                            icon={TrendingUp}
                            tooltip="Average number of entries per participant (Total Entries ÷ Participants)."
                        />
                        <KpiCard
                            title="Referred Users"
                            value={analytics?.summary.referred_participants || 0}
                            icon={ArrowUpRight}
                            tooltip="Number of participants who joined using another participant’s referral code."
                        />
                        <KpiCard
                            title="Bonus Entries"
                            value={analytics?.summary.referral_bonus_entries || 0}
                            icon={Gift}
                            tooltip="Total entries awarded from referral bonuses."
                        />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Chart: Entries Over Time */}
                        <Card className="col-span-1 lg:col-span-2 shadow-sm">
                            <CardHeader>
                                <CardTitle>Entries Over Time</CardTitle>
                            </CardHeader>
                            <CardContent className="h-[350px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={analytics?.entries_over_time}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                        <XAxis
                                            dataKey="date"
                                            fontSize={12}
                                            tickLine={false}
                                            axisLine={false}
                                            tickFormatter={(v) => format(new Date(v), 'MMM d')}
                                            minTickGap={30}
                                        />
                                        <YAxis fontSize={12} tickLine={false} axisLine={false} />
                                        <RechartsTooltip
                                            contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="entries"
                                            stroke="#0ea5e9"
                                            strokeWidth={3}
                                            dot={false}
                                            activeDot={{ r: 6 }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </Card>

                        {/* Chart: Top Entry Methods */}
                        <Card className="col-span-1 shadow-sm">
                            <CardHeader>
                                <CardTitle>Top Entry Methods</CardTitle>
                            </CardHeader>
                            <CardContent className="h-[350px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart layout="vertical" data={analytics?.top_entry_methods?.filter(entry => entry.key !== 'signup')} margin={{ left: 0, right: 30 }}>
                                        <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#E5E7EB" />
                                        <XAxis type="number" hide />
                                        <YAxis
                                            dataKey="label"
                                            type="category"
                                            fontSize={11}
                                            tickLine={false}
                                            axisLine={false}
                                            width={100}
                                        />
                                        <RechartsTooltip cursor={{ fill: 'transparent' }} />
                                        <Bar dataKey="entries" radius={[0, 4, 4, 0]} barSize={32}>
                                            {analytics?.top_entry_methods?.filter(entry => entry.key !== 'signup').map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Table: Top Referrers */}
                    <Card className="shadow-sm">
                        <CardHeader>
                            <CardTitle>Top Referrers</CardTitle>
                            <CardDescription>Participants driving the most traffic.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="rounded-md border">
                                <div className="grid grid-cols-12 bg-muted/50 p-3 text-sm font-medium text-muted-foreground">
                                    <div className="col-span-1">#</div>
                                    <div className="col-span-8">Email</div>
                                    <div className="col-span-3 text-right">Referrals</div>
                                </div>
                                {analytics?.top_referrers.map((referrer, idx) => (
                                    <div key={referrer.participant_id} className="grid grid-cols-12 p-3 text-sm border-t last:border-0 hover:bg-muted/5 transition-colors items-center">
                                        <div className="col-span-1 font-mono text-muted-foreground">{idx + 1}</div>
                                        <div className="col-span-8 font-medium truncate" title={referrer.email}>
                                            {referrer.email || "Unknown"}
                                        </div>
                                        <div className="col-span-3 text-right font-bold text-primary">
                                            {referrer.referred_count}
                                        </div>
                                    </div>
                                ))}
                                {(!analytics?.top_referrers || analytics.top_referrers.length === 0) && (
                                    <div className="p-8 text-center text-muted-foreground text-sm">
                                        No referrals yet.
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
}

function KpiCard({ title, value, icon: Icon, tooltip }: { title: string; value: number | string; icon: any; tooltip?: string }) {
    return (
        <Card className="shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-6 flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0 space-y-3">
                    <div className="flex items-center gap-1.5">
                        <p className="text-sm font-medium text-muted-foreground whitespace-nowrap">
                            {title}
                        </p>
                        {tooltip && (
                            <Tooltip delayDuration={300}>
                                <TooltipTrigger asChild>
                                    <button
                                        type="button"
                                        className="inline-flex shrink-0 cursor-help focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-full p-0.5 hover:bg-muted transition-colors"
                                    >
                                        <Info className="h-3.5 w-3.5 text-muted-foreground/70" />
                                        <span className="sr-only">About {title}</span>
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                    <p className="max-w-[200px] text-xs font-normal">{tooltip}</p>
                                </TooltipContent>
                            </Tooltip>
                        )}
                    </div>
                    <h2 className="text-2xl font-bold">{value}</h2>
                </div>
                <div className="shrink-0 p-3 bg-secondary/50 rounded-full text-foreground/80">
                    <Icon className="w-5 h-5" />
                </div>
            </CardContent>
        </Card>
    );
}
